import { useEffect, useState, useMemo } from 'react';
import { useSelector } from 'umi';
import { getSelector } from '@/pages/dashboard/services/dashboardSetting';
import { formatSortFiltersData } from '@/pages/dashboard/utils/_config/formatSortFiltersData';
import { IDashboardSettingState } from '../types/dashboard';

/**
 * 用于获取额外筛选器数据的 Hook
 * @param extraFilterList - 需要额外获取的筛选器列表，例如 ['ad_format', 'network_type']
 * @returns 合并了全局 selector 和额外 selector 的数据
 * 
 * @example
 * ```tsx
 * const PcConsoleDailyFilter = (props) => {
 *   const { config } = props;
 *   const { extraFilterList } = config || {};
 *   const mergedSelector = useExtraSelector(extraFilterList);
 *   
 *   return (
 *     <Filter outSelector={mergedSelector} {...otherProps} />
 *   );
 * };
 * ```
 */
interface IExtraSelector {
  [key: string]: unknown;
}

const useExtraSelector = (extraFilterList?: string[]) => {
  const [extraSelector, setExtraSelector] = useState<IExtraSelector | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const { gameCode, selector } = useSelector((state: IDashboardSettingState) => ({
    gameCode: state?.dashboard?.currentGame?.game_code,
    selector: state?.dashboardSetting?.selector,
  }));

  // 请求额外的筛选器数据
  useEffect(() => {
    if (!extraFilterList || extraFilterList.length === 0 || !gameCode) {
      setExtraSelector(null);
      return;
    }

    setLoading(true);
    getSelector(gameCode, undefined, extraFilterList)
      .then((data) => {
        const formattedData = formatSortFiltersData(data || {});
        setExtraSelector(formattedData);
      })
      .catch((error) => {
        console.error('获取额外筛选器失败:', error);
        setExtraSelector(null);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [extraFilterList, gameCode]);

  // 合并 selector 和 extraSelector
  const mergedSelector = useMemo(() => {
    if (!extraSelector || !selector) {
      return selector;
    }

    return {
      ...selector,
      ...extraSelector,
    };
  }, [selector, extraSelector]);

  return {
    mergedSelector,
    extraSelector,
    loading,
  };
};

export default useExtraSelector;
